import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Dumbbell, Lock, Copy } from "lucide-react";

const CATEGORIES = ["Strength", "Conditioning", "Mobility", "Core", "Beginner", "Advanced"];
const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];

const blank = () => ({
  id: null, title: "", category: "Strength", difficulty: "Beginner",
  duration_min: 30, equipment: "", description: "", coaching_notes: "",
  audience: "public", assigned_client_id: null,
  blocks: [{ name: "Main Sets", exercises: [{ name: "", sets: 3, reps: "10", notes: "" }] }],
});

const GLOBAL = "__global__";

export default function AdminWorkouts() {
  const [workouts, setWorkouts] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "Unknown client";

  const load = () => api.get("/workouts").then(({ data }) => setWorkouts(data)).catch(() => {});
  const loadClients = () => api.get("/admin/users")
    .then(({ data }) => setClients(data.filter((u) => u.subscription_tier === "coaching")))
    .catch(() => {});
  useEffect(() => { load(); loadClients(); }, []);

  const openNew = () => { setForm(blank()); setOpen(true); };
  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/workouts/${id}`);
      setForm({ ...data, equipment: (data.equipment || []).join(", "), blocks: data.blocks?.length ? data.blocks : blank().blocks });
      setOpen(true);
    } catch (e) { toast.error("Could not load workout"); }
  };

  // block helpers
  const addBlock = () => set("blocks", [...form.blocks, { name: "New Block", exercises: [{ name: "", sets: 3, reps: "10", notes: "" }] }]);
  const removeBlock = (bi) => set("blocks", form.blocks.filter((_, i) => i !== bi));
  const setBlockName = (bi, v) => set("blocks", form.blocks.map((b, i) => i === bi ? { ...b, name: v } : b));
  const addEx = (bi) => set("blocks", form.blocks.map((b, i) => i === bi ? { ...b, exercises: [...b.exercises, { name: "", sets: 3, reps: "10", notes: "" }] } : b));
  const removeEx = (bi, ei) => set("blocks", form.blocks.map((b, i) => i === bi ? { ...b, exercises: b.exercises.filter((_, j) => j !== ei) } : b));
  const setEx = (bi, ei, k, v) => set("blocks", form.blocks.map((b, i) => i === bi ? { ...b, exercises: b.exercises.map((e, j) => j === ei ? { ...e, [k]: v } : e) } : b));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        id: form.id || undefined,
        duration_min: parseInt(form.duration_min) || 0,
        equipment: form.equipment ? form.equipment.split(",").map((s) => s.trim()).filter(Boolean) : [],
        blocks: form.blocks.map((b) => ({
          name: b.name,
          exercises: b.exercises.filter((e) => e.name.trim()).map((e) => ({ ...e, sets: parseInt(e.sets) || 0 })),
        })),
      };
      await api.post("/admin/workouts", payload);
      toast.success(form.id ? "Workout updated" : "Workout created");
      setOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this workout?")) return;
    try { await api.delete(`/admin/workouts/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Delete failed"); }
  };

  // Duplicate an existing workout into a new editable copy (new id, "(Copy)" title).
  const clone = async (id) => {
    try {
      const { data } = await api.get(`/workouts/${id}`);
      const { id: _omit, ...rest } = data; // drop id so the backend generates a fresh one
      const payload = {
        ...rest,
        title: `${data.title} (Copy)`,
        equipment: Array.isArray(data.equipment) ? data.equipment : [],
        blocks: data.blocks?.length ? data.blocks : [],
      };
      await api.post("/admin/workouts", payload);
      toast.success("Workout cloned");
      load();
    } catch (e) { toast.error("Clone failed"); }
  };

  return (
    <div className="space-y-3 mt-4" data-testid="admin-workouts">
      <Button data-testid="admin-new-workout" onClick={openNew} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold"><Plus className="h-4 w-4 mr-1" /> New workout</Button>

      {workouts.map((w) => (
        <Card key={w.id} data-testid={`admin-workout-${w.id}`} className="rounded-xl bg-card border-border p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Dumbbell className="h-5 w-5 text-[#ccff0c]" />
            <div>
              <p className="font-semibold text-sm">{w.title}</p>
              <div className="flex gap-1.5 mt-0.5"><Badge className="bg-secondary border border-border text-muted-foreground text-[10px]">{w.category}</Badge><Badge className="bg-secondary border border-border text-muted-foreground text-[10px]">{w.difficulty}</Badge>{w.assigned_client_id && (<Badge data-testid={`exclusive-badge-${w.id}`} className="bg-[#ccff0c]/15 text-[#ccff0c] border border-[#ccff0c]/30 text-[10px] flex items-center gap-0.5"><Lock className="h-2.5 w-2.5" /> {clientName(w.assigned_client_id)}</Badge>)}{!w.assigned_client_id && (w.audience === "coaching" || w.audience === "both") && (<Badge data-testid={`audience-badge-${w.id}`} className="bg-[#ccff0c]/15 text-[#ccff0c] border border-[#ccff0c]/30 text-[10px] flex items-center gap-0.5"><Lock className="h-2.5 w-2.5" /> {w.audience === "both" ? "Public + 1-on-1" : "1-on-1 only"}</Badge>)}</div>
            </div>
          </div>
          <div className="flex gap-1">
            <button data-testid={`edit-workout-${w.id}`} onClick={() => openEdit(w.id)} className="p-2 text-muted-foreground hover:text-[#ccff0c]"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`clone-workout-${w.id}`} onClick={() => clone(w.id)} title="Clone workout" className="p-2 text-muted-foreground hover:text-[#ccff0c]"><Copy className="h-4 w-4" /></button>
            <button data-testid={`delete-workout-${w.id}`} onClick={() => del(w.id)} className="p-2 text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        </Card>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover border-border max-w-[460px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{form.id ? "Edit workout" : "New workout"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title</Label><Input data-testid="wf-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-card border-border h-10" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="wf-category" className="bg-card border-border h-10"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Difficulty</Label>
                <Select value={form.difficulty} onValueChange={(v) => set("difficulty", v)}><SelectTrigger data-testid="wf-difficulty" className="bg-card border-border h-10"><SelectValue /></SelectTrigger><SelectContent>{DIFFICULTIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Duration (min)</Label><Input data-testid="wf-duration" type="number" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} className="bg-card border-border h-10" /></div>
              <div><Label className="text-xs">Equipment (comma)</Label><Input data-testid="wf-equipment" value={form.equipment} onChange={(e) => set("equipment", e.target.value)} placeholder="Barbell, Bench" className="bg-card border-border h-10" /></div>
            </div>
            <div><Label className="text-xs">Description</Label><Textarea data-testid="wf-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-card border-border" /></div>
            <div><Label className="text-xs">Coaching notes</Label><Textarea data-testid="wf-notes" value={form.coaching_notes} onChange={(e) => set("coaching_notes", e.target.value)} className="bg-card border-border" /></div>

            {/* Audience (group-level) */}
            <div>
              <Label className="text-xs">Audience</Label>
              <Select value={form.audience || "public"} onValueChange={(v) => set("audience", v)}>
                <SelectTrigger data-testid="wf-audience" className="bg-card border-border h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border text-foreground">
                  <SelectItem value="public">Public / Global (all members)</SelectItem>
                  <SelectItem value="coaching">1-on-1 clients only</SelectItem>
                  <SelectItem value="both">Both (public + 1-on-1 clients)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">
                "1-on-1 clients only" shows in coaching clients' Exclusive tab. "Both" shows in the public library and the Exclusive tab.
              </p>
            </div>

            {/* Visibility / individual assignment (optional, more granular) */}
            <div>
              <Label className="text-xs">Assign to a specific client (optional)</Label>
              <Select
                value={form.assigned_client_id || GLOBAL}
                onValueChange={(v) => set("assigned_client_id", v === GLOBAL ? null : v)}
              >
                <SelectTrigger data-testid="wf-assign-client" className="bg-card border-border h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border text-foreground">
                  <SelectItem value={GLOBAL}>No specific client (use Audience above)</SelectItem>
                  {clients.length === 0 ? (
                    <SelectItem value="__none__" disabled>No 1-on-1 coaching members yet</SelectItem>
                  ) : (
                    clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>Exclusive → {c.name} ({c.email})</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Assigning to one client overrides Audience — only that client sees it.</p>
            </div>

            {/* Blocks editor */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Blocks & exercises</Label>
                <Button data-testid="wf-add-block" onClick={addBlock} size="sm" className="h-7 bg-secondary border border-border text-foreground text-xs"><Plus className="h-3 w-3 mr-1" />Block</Button>
              </div>
              {form.blocks.map((b, bi) => (
                <div key={bi} className="rounded-xl border border-border bg-muted p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input data-testid={`wf-block-name-${bi}`} value={b.name} onChange={(e) => setBlockName(bi, e.target.value)} className="bg-card border-border h-9 text-sm" />
                    <button onClick={() => removeBlock(bi)} className="p-2 text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  {b.exercises.map((ex, ei) => (
                    <div key={ei} className="flex gap-1.5 items-center">
                      <Input data-testid={`wf-ex-name-${bi}-${ei}`} value={ex.name} onChange={(e) => setEx(bi, ei, "name", e.target.value)} placeholder="Exercise" className="bg-card border-border h-9 text-sm flex-1" />
                      <Input value={ex.sets} onChange={(e) => setEx(bi, ei, "sets", e.target.value)} placeholder="sets" className="bg-card border-border h-9 text-sm w-14" />
                      <Input value={ex.reps} onChange={(e) => setEx(bi, ei, "reps", e.target.value)} placeholder="reps" className="bg-card border-border h-9 text-sm w-16" />
                      <button onClick={() => removeEx(bi, ei)} className="p-1.5 text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <Button data-testid={`wf-add-ex-${bi}`} onClick={() => addEx(bi)} size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground"><Plus className="h-3 w-3 mr-1" />Exercise</Button>
                </div>
              ))}
            </div>

            <Button data-testid="wf-save" onClick={save} disabled={saving} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">{saving ? "Saving…" : "Save workout"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
